import { motion } from 'framer-motion';
import { useLang } from '@/context/LanguageContext';
import { StudioGallery } from './StudioGallery';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const equipmentCategories = [
  {
    category: 'Console & Recording',
    items: [
      { name: 'Neve VR 24', type: 'Console' },
      { name: 'ProTools Ultimate HDX', type: 'DAW' },
      { name: 'AVID HD 32x32', type: 'Interface' },
      { name: 'Mac M2 2023', type: 'Computer' },
      { name: 'Antelope Isochrone OCX', type: 'Clock' },
    ],
  },
  {
    category: 'Monitoring',
    items: [
      { name: 'Genelec 1031A', type: '3 pairs' },
      { name: 'Meyer Sound HD-1', type: 'Reference' },
      { name: 'Yamaha NS-10', type: 'Classic' },
      { name: 'Avantone MixCube', type: 'Near-field' },
    ],
  },
  {
    category: 'Microphones',
    items: [
      { name: 'Neumann U67', type: 'Tube (x2)' },
      { name: 'Sony C800G', type: 'Tube' },
      { name: 'Neumann U87 / U87ai', type: 'Condenser' },
      { name: 'Neumann M149', type: 'Tube (x2)' },
      { name: 'Neumann KM84', type: 'Small Diaphragm' },
      { name: 'AKG C414 EB / B-ULS', type: 'Condenser' },
      { name: 'Coles 4038', type: 'Ribbon (x2)' },
      { name: 'Royer R121', type: 'Ribbon (x2)' },
    ],
  },
  {
    category: 'Outboard Gear',
    items: [
      { name: 'Urei 1176 LN', type: 'Compressor (x3)' },
      { name: 'Teletronix LA-2A', type: 'Compressor (x2)' },
      { name: 'GML 8900', type: 'Dynamic EQ' },
      { name: 'Lexicon 480L', type: 'Reverb' },
      { name: 'AMS RMX16', type: 'Reverb' },
      { name: 'Eventide H3000', type: 'Multi-FX' },
      { name: 'Pultec EQH-2', type: 'EQ' },
    ],
  },
  {
    category: 'Backline',
    items: [
      { name: 'Yamaha C3', type: 'Grand Piano' },
      { name: 'Rhodes Suitcase 88', type: 'Electric Piano' },
      { name: 'Wurlitzer 200A', type: 'Electric Piano' },
      { name: 'Marshall JCM 800', type: 'Guitar Amp' },
      { name: 'Vox AC30', type: 'Guitar Amp' },
      { name: 'Fender Deluxe', type: 'Guitar Amp' },
      { name: 'Ampeg B18', type: 'Bass Amp' },
    ],
  },
];

const studioTranslations = {
  label: { fr: 'Le Studio', en: 'The Studio' },
  title: { fr: 'Studio d\'enregistrement professionnel', en: 'Professional Recording Studio' },
  desc: {
    fr: 'Console Neve VR 24, microphones vintage, compresseurs légendaires, réverbes et backline complet. Acoustique conçue par des ingénieurs spécialisés.',
    en: 'Neve VR 24 console, vintage microphones, legendary compressors, reverbs, and complete backline. Acoustics designed by specialist engineers.',
  },
};

const StudioEquipmentSection = () => {
  const { lang } = useLang();
  const tx = studioTranslations;

  return (
    <section id="studio" data-testid="equipment-section" className="py-24 md:py-32 bg-[#F5F1EC]">
      <div className="px-5 md:px-10 lg:px-14 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-2xl mb-14"
        >
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#888] mb-4">{tx.label[lang]}</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#111] leading-tight mb-5">
            {tx.title[lang]}
          </h2>
          <p className="text-base text-[#555] leading-relaxed">
            {tx.desc[lang]}
          </p>
        </motion.div>

        <StudioGallery />

        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="space-y-3">
            {equipmentCategories.map((category, index) => (
              <AccordionItem
                key={category.category}
                value={`item-${index}`}
                data-testid={`equipment-category-${index}`}
                className="bg-[#F5F1EC] border border-[#D9D3CC] transition-colors duration-300 rounded overflow-hidden"
              >
                <AccordionTrigger className="px-5 py-4 hover:no-underline">
                  <span className="font-heading text-base font-bold text-[#111]">
                    {category.category}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {category.items.map((item, itemIndex) => (
                      <div
                        key={itemIndex}
                        data-testid={`equipment-item-${index}-${itemIndex}`}
                        className="flex justify-between items-center py-2 border-b border-[#D9D3CC] last:border-0"
                      >
                        <span className="text-[#111] font-medium text-sm">{item.name}</span>
                        <span className="text-xs text-[#888] font-mono">{item.type}</span>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>

    </section>
  );
};

export default StudioEquipmentSection;
